import * as THREE from 'three';
import { CORE_APPEARANCE } from './coreAppearance';

// A closed, rounded volume with a heart silhouette and deliberately broad
// facets. Both sides have depth, so the reveal holds up as the camera moves.
const createHeartGeometry = () => {
  const segments = 48;
  const rows = 7;
  const positions = [];
  const indices = [];
  for (let side = 0; side < 2; side += 1) {
    for (let row = 0; row <= rows; row += 1) {
      const radius = row / rows;
      for (let segment = 0; segment < segments; segment += 1) {
        const angle = (segment / segments) * Math.PI * 2;
        const x = (16 * Math.pow(Math.sin(angle), 3)) / 18;
        const y =
          (13 * Math.cos(angle) -
            5 * Math.cos(2 * angle) -
            2 * Math.cos(3 * angle) -
            Math.cos(4 * angle)) /
            18 +
          0.1;
        positions.push(
          x * radius,
          y * radius,
          (side === 0 ? 1 : -1) * 0.44 * Math.sqrt(1 - radius * radius)
        );
        if (row < rows) {
          const a = side * (rows + 1) * segments + row * segments + segment;
          const b = a - segment + ((segment + 1) % segments);
          const c = a + segments;
          const d = b + segments;
          indices.push(
            ...(side === 0 ? [a, b, c, b, d, c] : [a, c, b, b, c, d])
          );
        }
      }
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(positions, 3)
  );
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  return geometry;
};

const createGlowTexture = () => {
  const size = 64;
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const radius = Math.hypot((x - 31.5) / 31.5, (y - 31.5) / 31.5);
      const offset = (y * size + x) * 4;
      data[offset] = data[offset + 1] = data[offset + 2] = 255;
      data[offset + 3] = Math.round(
        255 * Math.pow(Math.max(0, 1 - radius), 2.6)
      );
    }
  }
  const texture = new THREE.DataTexture(data, size, size);
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.needsUpdate = true;
  return texture;
};

const glowMaterial = (color, opacity = 1) =>
  new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false,
  });

const ARM_COUNT = 6;
const ARM_SEGMENTS = 16;

export const createLivingHeart = (core) => {
  const group = new THREE.Group();
  group.visible = false;
  core.add(group);
  const beatRoot = new THREE.Group();
  group.add(beatRoot);
  const material = new THREE.MeshPhysicalMaterial({
    color: 0xa71f56,
    emissive: 0xa50d41,
    emissiveIntensity: 0.5,
    metalness: 0.5,
    roughness: 0.3,
    clearcoat: 0.8,
    flatShading: true,
  });
  const body = new THREE.Mesh(createHeartGeometry(), material);
  body.castShadow = true;
  beatRoot.add(body);

  const veinMaterial = glowMaterial(0xff92b3);
  const veins = [
    [
      [0, 0.4, 0.4],
      [-0.09, 0.2, 0.46],
      [0.06, 0, 0.46],
      [-0.09, -0.23, 0.43],
      [0, -0.57, 0.32],
    ],
    [
      [-0.08, 0.2, 0.46],
      [-0.3, 0.33, 0.43],
      [-0.46, 0.51, 0.35],
    ],
    [
      [0.03, 0.04, 0.46],
      [0.28, 0.19, 0.44],
      [0.55, 0.35, 0.35],
    ],
    [
      [-0.07, -0.24, 0.43],
      [-0.32, -0.18, 0.41],
      [-0.51, -0.06, 0.35],
    ],
    [
      [-0.01, -0.4, 0.38],
      [0.2, -0.3, 0.4],
      [0.37, -0.2, 0.37],
    ],
  ];
  veins.forEach((points, index) => {
    const curve = new THREE.CatmullRomCurve3(
      points.map((point) => new THREE.Vector3(...point))
    );
    beatRoot.add(
      new THREE.Mesh(
        new THREE.TubeGeometry(curve, 14, index === 0 ? 0.018 : 0.01, 4, false),
        veinMaterial
      )
    );
  });

  // Recessed, inward-slanting sensor vents give the heart a fixed, hostile
  // gaze. Their hot inner surfaces brighten as it charges an attack.
  const eyeMaterial = new THREE.MeshBasicMaterial({ color: 0x210610 });
  const pupilMaterial = glowMaterial(0xff9cba);
  [-1, 1].forEach((side) => {
    const recess = new THREE.Shape();
    recess.moveTo(side * 0.18, 0.18);
    recess.lineTo(side * 0.61, 0.4);
    recess.lineTo(side * 0.51, 0.2);
    recess.lineTo(side * 0.24, 0.08);
    recess.closePath();
    const eye = new THREE.Mesh(new THREE.ShapeGeometry(recess), eyeMaterial);
    eye.position.z = 0.475;
    beatRoot.add(eye);
    const slit = new THREE.Shape();
    slit.moveTo(side * 0.25, 0.17);
    slit.lineTo(side * 0.53, 0.3);
    slit.lineTo(side * 0.46, 0.2);
    slit.closePath();
    const pupil = new THREE.Mesh(new THREE.ShapeGeometry(slit), pupilMaterial);
    pupil.position.z = 0.48;
    beatRoot.add(pupil);
  });

  const aura = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: createGlowTexture(),
      color: 0xff326d,
      transparent: true,
      opacity: 0.35,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    })
  );
  aura.position.z = -0.35;
  aura.scale.set(4.6, 4.6, 1);
  group.add(aura);

  // Tapered segments form six continuous articulated cables. Instances keep
  // the animated limbs inexpensive on phones without rebuilding geometry.
  const armMaterial = new THREE.MeshStandardMaterial({
    color: 0x5d123a,
    emissive: 0xff326d,
    emissiveIntensity: 0.4,
    metalness: 0.75,
    roughness: 0.28,
  });
  const arms = new THREE.InstancedMesh(
    new THREE.CylinderGeometry(0.7, 1, 1, 5),
    armMaterial,
    ARM_COUNT * ARM_SEGMENTS
  );
  arms.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  arms.frustumCulled = false;
  group.add(arms);
  const tipMaterial = glowMaterial(0xff80a7);
  const tipGeometry = new THREE.ConeGeometry(0.07, 0.24, 5);
  const tips = Array.from({ length: ARM_COUNT }, () => {
    const mesh = new THREE.Mesh(tipGeometry, tipMaterial);
    group.add(mesh);
    return mesh;
  });
  const crownMaterial = new THREE.MeshStandardMaterial({
    color: 0x67102d,
    emissive: 0xff3e25,
    emissiveIntensity: 0.65,
    metalness: 0.75,
    roughness: 0.25,
    flatShading: true,
  });
  const crownGeometry = new THREE.ConeGeometry(0.115, 0.6, 4);
  const crown = Array.from({ length: 12 }, (_, index) => {
    const mesh = new THREE.Mesh(crownGeometry, crownMaterial);
    mesh.userData.angle = (index / 12) * Math.PI * 2;
    group.add(mesh);
    return mesh;
  });

  const pulseMaterial = glowMaterial(0xff8b65, 0);
  const pulseGeometry = new THREE.TorusGeometry(1, 0.012, 5, 72);
  const pulses = Array.from({ length: 3 }, () => {
    const mesh = new THREE.Mesh(pulseGeometry, pulseMaterial.clone());
    mesh.position.z = -0.2;
    group.add(mesh);
    return mesh;
  });
  pulseMaterial.dispose();
  const shockwave = new THREE.Mesh(pulseGeometry, glowMaterial(0xffb9ce, 0));
  shockwave.position.z = 0.5;
  group.add(shockwave);

  const emberPositions = new Float32Array(64 * 3);
  const emberGeometry = new THREE.BufferGeometry();
  emberGeometry.setAttribute(
    'position',
    new THREE.BufferAttribute(emberPositions, 3).setUsage(
      THREE.DynamicDrawUsage
    )
  );
  const embers = new THREE.Points(
    emberGeometry,
    new THREE.PointsMaterial({
      color: 0xff926a,
      size: 0.034,
      transparent: true,
      opacity: 0.7,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    })
  );
  embers.frustumCulled = false;
  group.add(embers);

  const parentRotation = new THREE.Quaternion();
  const rotation = new THREE.Quaternion();
  const matrix = new THREE.Matrix4();
  const start = new THREE.Vector3();
  const end = new THREE.Vector3();
  const midpoint = new THREE.Vector3();
  const direction = new THREE.Vector3();
  const segmentScale = new THREE.Vector3();
  const up = new THREE.Vector3(0, 1, 0);
  const energyColor = new THREE.Color();
  const bodyColor = new THREE.Color();
  const emissiveColor = new THREE.Color();
  const feralBodyColor = new THREE.Color(CORE_APPEARANCE.meltdown.color);
  const feralEmissiveColor = new THREE.Color(CORE_APPEARANCE.meltdown.emissive);
  const feralEnergyColor = new THREE.Color(CORE_APPEARANCE.meltdown.accent);

  const update = ({
    elapsed,
    evolution,
    meltdown,
    transition = -1,
    stage,
    progress = 0,
    camera,
    faceCamera = true,
    reduceMotion,
    visible = true,
  }) => {
    group.visible = visible && evolution > 0.001;
    if (!group.visible) return;
    if (faceCamera) {
      // Cinematic views keep their readable, camera-facing silhouette.
      core.getWorldQuaternion(parentRotation);
      group.quaternion
        .copy(parentRotation.invert())
        .multiply(camera.quaternion);
    } else {
      // On the board, inherit the Core's physical tilt toward the player's die.
      group.quaternion.identity();
    }
    const time = reduceMotion ? 0 : elapsed;
    const period = THREE.MathUtils.lerp(0.92, 0.5, meltdown);
    const cycle = (time % period) / period;
    const beat = reduceMotion
      ? 0
      : Math.exp(-Math.pow((cycle - 0.12) / 0.075, 2)) +
        0.48 * Math.exp(-Math.pow((cycle - 0.32) / 0.095, 2));
    const charging = stage === 'bossCharge' ? progress : 0;
    const lash =
      stage === 'bossFire' && !reduceMotion ? Math.sin(progress * Math.PI) : 0;
    const growth = evolution * (1.12 + meltdown * 0.2);
    group.scale.setScalar(growth);
    beatRoot.scale.set(
      1 + beat * 0.12 - charging * 0.06,
      1 + beat * 0.08,
      1 + beat * 0.16
    );
    beatRoot.rotation.z = Math.sin(time * 1.6) * (0.035 + meltdown * 0.035);
    beatRoot.position.z = lash * (0.12 + meltdown * 0.18);
    bodyColor
      .setHex(CORE_APPEARANCE.evolution.color)
      .lerp(feralBodyColor, meltdown);
    emissiveColor
      .setHex(CORE_APPEARANCE.evolution.emissive)
      .lerp(feralEmissiveColor, meltdown);
    energyColor
      .setHex(CORE_APPEARANCE.evolution.accent)
      .lerp(feralEnergyColor, meltdown);
    material.color.copy(bodyColor);
    material.emissive.copy(emissiveColor);
    material.emissiveIntensity = 0.45 + beat * 0.65 + charging * 0.8;
    veinMaterial.color.copy(energyColor);
    veinMaterial.opacity = 0.55 + beat * 0.35;
    pupilMaterial.color.copy(energyColor);
    pupilMaterial.opacity = 0.65 + charging * 0.35;
    aura.material.color.copy(emissiveColor);
    aura.material.opacity = 0.32 + beat * 0.12 + meltdown * 0.12;
    aura.scale.setScalar(4.6 + beat * 0.3 + charging * 0.6);
    armMaterial.emissive.copy(emissiveColor);
    armMaterial.emissiveIntensity = 0.55 + meltdown * 0.45 + beat * 0.25;
    tipMaterial.color.copy(energyColor);
    crownMaterial.emissive.copy(emissiveColor);

    const pointOnArm = (arm, t, target) => {
      const side = arm % 2 ? -1 : 1;
      const angle =
        (arm / ARM_COUNT) * Math.PI * 2 +
        Math.PI / 6 +
        side * t * (0.4 + Math.sin(time * 1.3 + arm) * 0.22) +
        Math.sin(time * (1.4 + meltdown) + t * 3 + arm) * t * 0.13;
      const radius = 0.67 + t * (0.85 + meltdown * 0.55) - charging * t * 0.36;
      target.set(
        Math.cos(angle) * radius,
        Math.sin(angle) * radius * 0.88,
        -0.1 +
          Math.sin(t * 3 + time * 1.7 + arm) * t * 0.13 +
          t * t * (0.35 + meltdown * 0.35 + lash * 1.5)
      );
    };
    for (let arm = 0; arm < ARM_COUNT; arm += 1) {
      pointOnArm(arm, 0, start);
      for (let segment = 0; segment < ARM_SEGMENTS; segment += 1) {
        const t = (segment + 1) / ARM_SEGMENTS;
        pointOnArm(arm, t, end);
        direction.copy(end).sub(start);
        const length = direction.length();
        rotation.setFromUnitVectors(up, direction.normalize());
        midpoint.copy(start).add(end).multiplyScalar(0.5);
        const thickness = (0.045 + meltdown * 0.016) * (1 - t * 0.72);
        segmentScale.set(thickness, length * 1.18, thickness);
        matrix.compose(midpoint, rotation, segmentScale);
        arms.setMatrixAt(arm * ARM_SEGMENTS + segment, matrix);
        start.copy(end);
      }
      tips[arm].position.copy(end);
      tips[arm].quaternion.copy(rotation);
      tips[arm].scale.setScalar(0.65 + meltdown * 0.55);
    }
    arms.instanceMatrix.needsUpdate = true;
    crown.forEach((spike, index) => {
      spike.visible = meltdown > 0.01;
      const angle = spike.userData.angle;
      const radius = 0.98 + meltdown * 0.15 + beat * 0.04;
      spike.position.set(
        Math.cos(angle) * radius,
        Math.sin(angle) * radius,
        -0.28
      );
      spike.rotation.set(0.2 * Math.sin(angle), 0, angle - Math.PI / 2);
      spike.scale.set(
        meltdown,
        meltdown * (index % 3 === 0 ? 1.5 : 0.9),
        meltdown
      );
    });
    pulses.forEach((pulse, index) => {
      pulse.visible = !reduceMotion;
      const travel = (time / (period * 2) + index / 3) % 1;
      pulse.scale.setScalar(0.9 + travel * (1.05 + meltdown * 0.65));
      pulse.material.color.copy(energyColor);
      pulse.material.opacity =
        Math.sin(travel * Math.PI) * (1 - travel) * (0.16 + meltdown * 0.1);
    });
    shockwave.visible = transition >= 0 && transition < 1 && !reduceMotion;
    shockwave.scale.setScalar(0.2 + Math.max(0, transition) * 5);
    shockwave.material.color.copy(energyColor);
    shockwave.material.opacity =
      Math.pow(1 - Math.max(0, transition), 2) * 0.75;
    embers.visible = !reduceMotion;
    embers.material.color.copy(energyColor);
    for (let index = 0; index < 64; index += 1) {
      const travel = (time * (0.15 + meltdown * 0.15) + index / 64) % 1;
      const angle = index * 2.39996 + time * 0.1;
      const radius = 0.8 + travel * (1 + meltdown);
      emberPositions[index * 3] = Math.cos(angle) * radius;
      emberPositions[index * 3 + 1] =
        Math.sin(angle) * radius * 0.8 + travel * 0.5;
      emberPositions[index * 3 + 2] = -0.4 + Math.sin(index * 13.7) * 0.4;
    }
    emberGeometry.attributes.position.needsUpdate = true;
  };

  return { group, body, update };
};
